import React, { useState } from "react";
import { Button, Card, Badge, Avatar, Field, Input, Select, Tabs, Drawer, Icon, IconBtn, EmptyState, PageHeader, Table, Spinner, Alert, Stat, Modal, ModalBody, ModalFooter } from "../../design-system";

const ROLES = [
  { id: "owner", name: "Owner", desc: "Control total · facturación", count: 1, color: "#7c3aed" },
  { id: "admin", name: "Admin", desc: "Configuración y datos sensibles", count: 3, color: "#2563eb" },
  { id: "manager", name: "Manager", desc: "Aprobación y supervisión de equipos", count: 12, color: "#3dc53a" },
  { id: "employee", name: "Employee", desc: "Marcaje propio y solicitudes", count: 136, color: "#6c757d" },
];

const PERMISSIONS = [
  { module: "Jornadas", items: [
    { name: "Marcar jornada propia", desc: "Iniciar/pausar/cerrar mi jornada", values: [true, true, true, true] },
    { name: "Ver jornadas del equipo", desc: "Acceso al estado en vivo del equipo", values: [true, true, true, false] },
    { name: "Editar jornadas", desc: "Modificar marcajes con justificación", values: [true, true, true, false], sensitive: true },
  ]},
  { module: "Solicitudes", items: [
    { name: "Crear solicitudes", values: [true, true, true, true] },
    { name: "Aprobar solicitudes", desc: "Vacaciones, asuntos propios, etc.", values: [true, true, true, false] },
    { name: "Aprobar bajas médicas", desc: "Requiere documentación adjunta", values: [true, true, false, false], sensitive: true },
  ]},
  { module: "Empleados", items: [
    { name: "Ver fichas de empleados", values: [true, true, true, false] },
    { name: "Crear / editar empleados", values: [true, true, false, false] },
    { name: "Ver salarios y nóminas", values: [true, true, false, false], sensitive: true },
    { name: "Eliminar empleados", values: [true, false, false, false], sensitive: true },
  ]},
  { module: "Reportes", items: [
    { name: "Generar reportes propios", values: [true, true, true, true] },
    { name: "Generar reportes de equipo", values: [true, true, true, false] },
    { name: "Firmar reportes oficiales", desc: "Sello legal", values: [true, true, false, false], sensitive: true },
  ]},
  { module: "Configuración", items: [
    { name: "Acceder a configuración", values: [true, true, false, false] },
    { name: "Modificar políticas de jornada", values: [true, true, false, false], sensitive: true },
    { name: "Gestionar facturación", values: [true, false, false, false], sensitive: true },
    { name: "Gestionar roles y permisos", values: [true, false, false, false], sensitive: true },
  ]},
];

function RolesPermisos() {
  document.title = "Roles y permisos | Kronaly";
  return (
    <div className="kr-page">
      <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Configuración · Workspace</div>
            <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Roles y permisos</h1>
            <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)", maxWidth: 640 }}>
              Define qué puede hacer cada rol. Los permisos sensibles requieren confirmación adicional.
            </p>
          </div>
          <Button variant="secondary" icon="Plus">Crear rol personalizado</Button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {ROLES.map(r => (
            <Card key={r.id} style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ height: 4, background: r.color }} />
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{r.name}</div>
                <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2, minHeight: 28 }}>{r.desc}</div>
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 18, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{r.count}</span>
                  <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>asignados</span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card padded={false}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>Matriz de permisos</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Badge tone="warning" dot>Permiso sensible</Badge>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "2.4fr repeat(4, 1fr)",
            padding: "10px 20px", background: "var(--kr-neutral-50)", borderBottom: "1px solid var(--kr-border)",
            fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, color: "var(--kr-fg-muted)" }}>
            <span>Permiso</span>
            {ROLES.map(r => <span key={r.id} style={{ textAlign: "center" }}>{r.name}</span>)}
          </div>

          {PERMISSIONS.map(group => (
            <div key={group.module}>
              <div style={{ padding: "12px 20px 6px", fontSize: 11, fontWeight: 700, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", background: "var(--kr-neutral-50)" }}>
                {group.module}
              </div>
              {group.items.map((p, i) => (
                <div key={p.name} style={{ display: "grid", gridTemplateColumns: "2.4fr repeat(4, 1fr)", alignItems: "center", padding: "10px 20px", borderBottom: "1px solid var(--kr-border-subtle)", fontSize: 13 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>{p.name}</span>
                      {p.sensitive && <Icon name="AlertTriangle" size={12} style={{ color: "var(--kr-warning-700)" }} title="Permiso sensible" />}
                    </div>
                    {p.desc && <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>{p.desc}</div>}
                  </div>
                  {p.values.map((v, j) => (
                    <div key={j} style={{ display: "flex", justifyContent: "center" }}>
                      <PermissionToggle on={v} sensitive={p.sensitive} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

function PermissionToggle({ on, sensitive }) {
  const [v, setV] = useState(on);
  return (
    <button onClick={() => setV(!v)} style={{
      width: 32, height: 18, borderRadius: 999, border: "none", cursor: "pointer",
      background: v ? (sensitive ? "var(--kr-warning-500)" : "var(--kr-action)") : "var(--kr-neutral-300)",
      position: "relative", padding: 0,
    }}>
      <span style={{ position: "absolute", top: 2, left: v ? 16 : 2, width: 14, height: 14, borderRadius: 999, background: "#fff", transition: "left .12s" }} />
    </button>
  );
}

export default RolesPermisos;
